import React, { useMemo, useState } from 'react';
import { Currency, formatMoney } from '../../../playground/common/format';
import { RequestResponseViewer } from '../../../playground/common/request-response-viewer';
import {
  ChartSeriesConfig,
  CurrencyHeader,
  EvolutionChart,
  KpiCard,
  KpiGrid,
  ProjectionColumn,
  ProjectionTable,
  SimulationWarnings,
  buildEvolutionSeries,
} from '../../../playground/endpoints/_shared';
import { ApvSelectOneRegime } from './schema';
import {
  ApvSelectOneMonthlyRow, ApvSelectOneResponse, ApvSelectOneYearlyRow,
} from './service';

// CONTEXT.md: `policy_value` is the Valor Póliza Efectivo, `vp_rescate` is
// what the client could actually withdraw today, `fiscal_bonus_balance` is
// the Cuenta de Bonificación Fiscal -- kept apart from `policy_value` by
// construction, never merged into it. The Fiscal Bonus only exists under
// Régimen A, so every series/column that reads it is dropped under B rather
// than plotted as a flat zero.
const CHART_SERIES: ChartSeriesConfig[] = [
  { dataKey: 'policy_value', name: 'Valor póliza (VP Efectivo)', stroke: 'var(--bs-primary)' },
  { dataKey: 'vp_rescate', name: 'VP Rescate', stroke: 'var(--bs-info)' },
  { dataKey: 'fiscal_bonus_balance', name: 'Cuenta Bonificación Fiscal', stroke: 'var(--bs-warning)' },
  {
    dataKey: 'death_compensation',
    name: 'Indemn. fallecimiento',
    stroke: 'var(--bs-success)',
    strokeDasharray: '5 4',
  },
];

const MONTHLY_CHART_SERIES: ChartSeriesConfig[] = [
  { dataKey: 'policy_value', name: 'Valor póliza (VP Efectivo)', stroke: 'var(--bs-primary)' },
  { dataKey: 'vp_rescate', name: 'VP Rescate', stroke: 'var(--bs-info)' },
];

const YEARLY_COLUMNS: ProjectionColumn<ApvSelectOneYearlyRow>[] = [
  { id: 'year', label: 'Año', isMoney: false },
  { id: 'age', label: 'Edad', isMoney: false },
  { id: 'actuarial_age', label: 'Edad actuarial', isMoney: false },
  { id: 'accumulated_prime', label: 'Prima acumulada', isMoney: true },
  { id: 'coverage_costs', label: 'Costo coberturas', isMoney: true },
  { id: 'policy_expenses', label: 'Gastos póliza', isMoney: true },
  { id: 'rentability', label: 'Rentado', isMoney: true },
  { id: 'policy_value', label: 'Valor póliza (VP Efectivo)', isMoney: true },
  { id: 'agreed_deposits_balance', label: 'Saldo depósitos convenidos', isMoney: true },
  { id: 'vp_rescate', label: 'VP Rescate', isMoney: true },
  { id: 'fiscal_bonus_balance', label: 'Cuenta Bonificación Fiscal', isMoney: true },
  { id: 'death_compensation', label: 'Indemn. fallecimiento', isMoney: true },
];

const isRegimeAOnly = (id: string) => id === 'fiscal_bonus_balance';

/**
 * `projection_by_month` rows with `fiscal_breakdown` flattened into plain
 * numeric columns. Both `ProjectionTable` and `buildEvolutionSeries` only
 * understand `Record<string, number | undefined>`, and the shared table's
 * popover-driven fiscal cell is keyed on v1's `fiscal_contributions_breakdown`
 * shape, which v2 does not emit -- so the nested benefit figures are hoisted
 * into their own columns here instead.
 */
interface ApvSelectOneMonthlyTableRow {
  month: number;
  calendar_month: number;
  // Benefit of the *active* regime: the Bonificación Fiscal under A, the
  // income-tax deduction under B. Only one of the two is ever present in the
  // response, so a single pair of columns serves both.
  regime_benefit?: number;
  regime_benefit_total?: number;
  regime_eligible_base?: number;
  regime_excess?: number;
  agreed_deposit_relief?: number;
  agreed_deposit_relief_total?: number;
  [key: string]: number | undefined;
}

function toMonthlyTableRow(
  row: ApvSelectOneMonthlyRow,
  regime: ApvSelectOneRegime,
): ApvSelectOneMonthlyTableRow {
  const breakdown = row.fiscal_breakdown;
  const relief = breakdown?.agreed_deposit_relief;
  const active = regime === 'A'
    ? breakdown?.bonificacion_fiscal
    : breakdown?.regime_b_deduction;
  // Single cast at the boundary: the spread carries `coverage_costs_breakdown`
  // through untouched (the shared table reads it off the row to render the
  // coverage-costs popover) even though it is not a number, and
  // `fiscal_breakdown` is replaced by the flattened columns below.
  return {
    ...row,
    fiscal_breakdown: undefined,
    regime_benefit: active?.benefit,
    regime_benefit_total: active?.running_total,
    regime_eligible_base: regime === 'A' ? active?.eligible_amount : active?.deductible_amount,
    regime_excess: active?.excess_amount,
    agreed_deposit_relief: relief?.benefit,
    agreed_deposit_relief_total: relief?.running_total,
  } as unknown as ApvSelectOneMonthlyTableRow;
}

function buildMonthlyColumns(
  regime: ApvSelectOneRegime,
): ProjectionColumn<ApvSelectOneMonthlyTableRow>[] {
  const shared = YEARLY_COLUMNS
    .filter((col) => col.id !== 'year' && !(regime === 'B' && isRegimeAOnly(col.id)))
    .map((col) => col as unknown as ProjectionColumn<ApvSelectOneMonthlyTableRow>);

  const fiscal: ProjectionColumn<ApvSelectOneMonthlyTableRow>[] = regime === 'A'
    ? [
      { id: 'regime_eligible_base', label: 'Base bonificable (año)', isMoney: true },
      { id: 'regime_benefit', label: 'Bonificación Fiscal del mes', isMoney: true },
      { id: 'regime_benefit_total', label: 'Bonificación Fiscal acumulada', isMoney: true },
    ]
    : [
      { id: 'regime_eligible_base', label: 'Base deducible (tope anual)', isMoney: true },
      { id: 'regime_excess', label: 'Exceso no deducible', isMoney: true },
      { id: 'regime_benefit', label: 'Rebaja de impuesto del mes', isMoney: true },
      { id: 'regime_benefit_total', label: 'Rebaja de impuesto acumulada', isMoney: true },
    ];

  return [
    { id: 'month', label: 'Mes', isMoney: false },
    ...shared,
    ...fiscal,
    { id: 'agreed_deposit_relief', label: 'Rebaja dep. convenidos del mes', isMoney: true },
    { id: 'agreed_deposit_relief_total', label: 'Rebaja dep. convenidos acumulada', isMoney: true },
  ];
}

function buildFiscalChartSeries(regime: ApvSelectOneRegime): ChartSeriesConfig[] {
  const series: ChartSeriesConfig[] = [
    {
      dataKey: 'regime_benefit_total',
      name: regime === 'A' ? 'Bonificación Fiscal acumulada' : 'Rebaja de impuesto acumulada',
      stroke: 'var(--bs-warning)',
    },
    {
      dataKey: 'agreed_deposit_relief_total',
      name: 'Rebaja dep. convenidos acumulada',
      stroke: 'var(--bs-secondary)',
      strokeDasharray: '5 4',
    },
  ];
  if (regime === 'A') {
    series.push({
      dataKey: 'fiscal_bonus_balance',
      name: 'Cuenta Bonificación Fiscal (con interés)',
      stroke: 'var(--bs-info)',
    });
  }
  return series;
}

interface FiscalKpisProps {
  regime: ApvSelectOneRegime;
  lastRow?: ApvSelectOneMonthlyTableRow;
  currency: Currency;
  ufValue: number;
}

function FiscalKpis({
  regime, lastRow, currency, ufValue,
}: FiscalKpisProps) {
  if (!lastRow) return null;
  const money = (value?: number) => formatMoney(value ?? 0, currency, ufValue);

  return (
    <div className="mb-4">
      <h6 className="text-muted text-uppercase small">
        {`Beneficio tributario — Régimen ${regime}`}
      </h6>
      <KpiGrid>
        <KpiCard
          label={regime === 'A' ? 'Bonificación Fiscal acumulada' : 'Rebaja de impuesto acumulada'}
          value={money(lastRow.regime_benefit_total)}
          hint={regime === 'A'
            ? 'Suma de lo acreditado en la sub-cuenta, sin su interés'
            : 'Menor impuesto a la renta — efectivo fuera de la póliza'}
          accent="text-warning"
        />
        {regime === 'A' ? (
          <KpiCard
            label="Cuenta Bonificación Fiscal"
            value={money(lastRow.fiscal_bonus_balance)}
            hint="Saldo final, ya con el interés de su propia sub-cuenta"
          />
        ) : (
          <KpiCard
            label="Exceso no deducible"
            value={money(lastRow.regime_excess)}
            hint="Ahorro voluntario por sobre el tope anual deducible"
            accent="text-danger"
          />
        )}
        <KpiCard
          label="Rebaja por depósitos convenidos"
          value={money(lastRow.agreed_deposit_relief_total)}
          hint="Plata del empleador — se acumula bajo ambos regímenes"
        />
      </KpiGrid>
    </div>
  );
}

interface ApvSelectOneResultViewProps {
  request: unknown;
  regime: ApvSelectOneRegime;
  response?: ApvSelectOneResponse;
  error?: unknown;
}

export function ApvSelectOneResultView({
  request,
  regime,
  response,
  error,
}: ApvSelectOneResultViewProps) {
  const [currency, setCurrency] = useState<Currency>('UF');
  const hasSuccess = !!response && !error;

  const monthlyRows = useMemo(
    () => (response?.projection_by_month ?? []).map((row) => toMonthlyTableRow(row, regime)),
    [response, regime],
  );
  const yearlyColumns = useMemo(
    () => YEARLY_COLUMNS.filter((col) => !(regime === 'B' && isRegimeAOnly(col.id))),
    [regime],
  );
  const yearlyChartSeries = useMemo(
    () => CHART_SERIES.filter((s) => !(regime === 'B' && isRegimeAOnly(s.dataKey))),
    [regime],
  );
  const monthlyColumns = useMemo(() => buildMonthlyColumns(regime), [regime]);
  const fiscalChartSeries = useMemo(() => buildFiscalChartSeries(regime), [regime]);

  return (
    <div className="mt-4">
      {hasSuccess && response && (
        <>
          <CurrencyHeader
            ufValue={response.uf_value}
            currency={currency}
            onCurrencyChange={setCurrency}
          />

          <SimulationWarnings warnings={response.warnings} />

          <div className="mb-4">
            <KpiGrid>
              <KpiCard
                label="Ahorro bruto"
                value={formatMoney(response.savings.gross, currency, response.uf_value)}
                hint={`${response.timeframe_months} meses`}
                accent="text-primary"
              />
              <KpiCard
                label="Interés ganado"
                value={formatMoney(response.savings.interest, currency, response.uf_value)}
              />
              <KpiCard
                label="Capital aportado neto"
                value={formatMoney(response.savings.net, currency, response.uf_value)}
              />
            </KpiGrid>
          </div>

          <div className="mb-4">
            <KpiGrid>
              <KpiCard
                label="Prima básica"
                value={formatMoney(response.premium.basic, currency, response.uf_value)}
              />
              <KpiCard
                label="Prima Pactada (acordada)"
                value={formatMoney(response.premium.agreed, currency, response.uf_value)}
              />
              <KpiCard
                label="Prima excedente"
                value={formatMoney(response.premium.excess, currency, response.uf_value)}
                accent="text-danger"
              />
            </KpiGrid>
          </div>

          <FiscalKpis
            regime={regime}
            lastRow={monthlyRows[monthlyRows.length - 1]}
            currency={currency}
            ufValue={response.uf_value}
          />

          <div className="mb-4">
            <EvolutionChart
              data={buildEvolutionSeries(
                response.projection_by_year,
                yearlyChartSeries.map((s) => s.dataKey),
                currency,
                response.uf_value,
              )}
              series={yearlyChartSeries}
              currency={currency}
            />
          </div>

          {monthlyRows.length > 0 && (
            <>
              <div className="mb-4">
                <EvolutionChart
                  title="Evolución mensual de la póliza"
                  data={buildEvolutionSeries(
                    monthlyRows,
                    ['policy_value', 'vp_rescate'],
                    currency,
                    response.uf_value,
                    ['month'],
                  )}
                  series={MONTHLY_CHART_SERIES}
                  currency={currency}
                  xKey="month"
                  xLabel="Mes"
                />
              </div>

              {/* Own chart rather than extra series on the policy chart: the
                  tax benefit is one or two orders of magnitude smaller than
                  the policy value and would be flat against it. */}
              <div className="mb-4">
                <EvolutionChart
                  title={`Beneficios tributarios acumulados — Régimen ${regime}`}
                  data={buildEvolutionSeries(
                    monthlyRows,
                    fiscalChartSeries.map((s) => s.dataKey),
                    currency,
                    response.uf_value,
                    ['month'],
                  )}
                  series={fiscalChartSeries}
                  currency={currency}
                  xKey="month"
                  xLabel="Mes"
                />
              </div>

              <div className="mb-4">
                <ProjectionTable
                  rows={monthlyRows}
                  columns={monthlyColumns}
                  currency={currency}
                  ufValue={response.uf_value}
                  title="Detalle mensual"
                  rowKey="month"
                />
              </div>
            </>
          )}

          <div className="mb-4">
            <ProjectionTable
              rows={response.projection_by_year}
              columns={yearlyColumns}
              currency={currency}
              ufValue={response.uf_value}
            />
          </div>
        </>
      )}

      <RequestResponseViewer request={request} response={response} error={error} />
    </div>
  );
}
